import {
  Button,
  DialogTrigger,
  LoadingIndicator,
} from "@light-technology/components";
import { Plus } from "@untitledui/icons";
import { useState } from "react";
import { api } from "../../state/api";
import {
  DeviceType,
  ProgramType,
  type AppAccount,
  type AppResponse,
} from "../../state/api.types";
import {
  findProgramDevice,
  getIsMissing,
  getNeedsAttention,
} from "../../utilities/devices";
import { DetailItem } from "../shared/DetailItem";
import { ManufacturerIcon } from "../shared/ManufacturerIcon";
import { getManufacturerDisplayName } from "../shared/manufacturers";
import { AddDeviceModal } from "./AddDeviceModal";
import { ComplianceChecklist } from "./ComplianceChecklist";
import { DeviceIcon } from "./DeviceStatuses";

type AccountSlideoutDevicesProps = {
  app: AppResponse;
  account: AppAccount;
};

export function AccountSlideoutDevices(props: AccountSlideoutDevicesProps) {
  const [isAddDeviceOpen, setIsAddDeviceOpen] = useState(false);

  const getDevices = api.useGetAccountDevicesQuery({
    accountUUID: props.account.uuid,
    appUUID: props.app.uuid,
  });

  const getPrograms = api.useGetAccountProgramAgreementsQuery({
    accountUUID: props.account.uuid,
    appUUID: props.app.uuid,
  });

  const devices = getDevices.data?.data ?? [];

  const programs = getPrograms.data?.data ?? [];

  if (getDevices.isLoading || getPrograms.isLoading) {
    return <LoadingIndicator type="brand-spinner" />;
  }

  const program_agreement = programs.at(0);
  if (!program_agreement) {
    return (
      <div className="flex flex-col items-center pr-3xl pb-3xl pl-3xl">
        <div className="text-md text-primary">No devices found</div>
        <div className="text-sm text-tertiary">
          Any connected devices will be found here.
        </div>
      </div>
    );
  }

  let manufacturer;
  let model;
  let device;
  let device_type;
  let identifier;
  let expected_device_types: DeviceType[] = [DeviceType.Battery];

  const program_type = program_agreement.program.program_type;
  switch (program_type) {
    case ProgramType.Battery:
      expected_device_types = [DeviceType.Battery];
      device = findProgramDevice(
        devices.filter((d) => d.device_type === DeviceType.Battery),
      );
      device_type = "Battery";
      if (device) {
        manufacturer = device.battery_details?.manufacturer;
        model = device.battery_details?.model;
        identifier = device.battery_details?.identifier;
      }
      break;
    case ProgramType.Evcharging:
      expected_device_types = [DeviceType.Evse, DeviceType.Vehicle];
      device = findProgramDevice(
        devices.filter((d) => expected_device_types.includes(d.device_type)),
      );
      // Either a charger or a vehicle satisfies the program, so the label only
      // narrows once we know which one is connected.
      device_type = "EV charging";
      if (device) {
        const isCharger = device.device_type === DeviceType.Evse;
        device_type = isCharger ? "EV charger" : "EV";
        // A vehicle carries its details on `vehicle_details`; `charger_details`
        // is only populated for EVSE devices.
        const details = isCharger
          ? device.charger_details
          : device.vehicle_details;
        manufacturer = details?.manufacturer;
        model = details?.model;
        identifier = details?.identifier;
      }
      break;
  }

  const deviceMissing = getIsMissing(device);
  const needsAttention = getNeedsAttention(device);

  return (
    <div className="flex flex-col gap-3xl rounded-xl border border-primary p-xl">
      <div className="flex items-center gap-lg">
        {deviceMissing ? (
          <DeviceIcon
            isMissing={true}
            needsAttention={false}
            programType={program_type}
          />
        ) : (
          <div className="-space-xs flex">
            <DeviceIcon
              isMissing={false}
              needsAttention={needsAttention}
              className="z-10 bg-primary"
              programType={program_type}
            />
            {manufacturer && (
              <ManufacturerIcon
                manufacturer={manufacturer}
                className="-ml-xs"
              />
            )}
          </div>
        )}
        <div>
          <div className="text-md text-secondary">
            {deviceMissing
              ? "Unknown"
              : manufacturer
                ? [getManufacturerDisplayName(manufacturer), model]
                    .filter(Boolean)
                    .join(" ")
                : device_type}
          </div>
          <div className="text-xs text-tertiary">{device_type}</div>
        </div>
      </div>
      <DetailItem
        label="Device Program"
        value={program_agreement.program.name}
        actions={[]}
      />
      {deviceMissing ? (
        <>
          <div className="text-sm">
            Customer is enrolled in a device program that requires a{" "}
            {device_type} device, but no device has been connected.
          </div>
          <DialogTrigger
            isOpen={isAddDeviceOpen}
            onOpenChange={setIsAddDeviceOpen}
          >
            <Button
              className="self-start"
              size="sm"
              iconLeading={Plus}
              onClick={() => {
                setIsAddDeviceOpen(true);
              }}
            >
              Add Device
            </Button>
            <AddDeviceModal
              account={props.account}
              app={props.app}
              onClose={() => {
                setIsAddDeviceOpen(false);
              }}
              deviceTypes={expected_device_types}
            />
          </DialogTrigger>
        </>
      ) : device ? (
        <>
          <ComplianceChecklist
            app={props.app}
            device={device}
            account={props.account}
          />
          <DetailItem label="UUID" value={device.uuid} actions={["copy"]} />
          <DetailItem
            label="Identifier"
            value={identifier}
            actions={["copy"]}
          />
          {program_type === ProgramType.Battery ? (
            <DetailItem
              label="Capacity"
              value={
                device.battery_details?.capacity_kwh
                  ? `${device.battery_details.capacity_kwh} kWh`
                  : undefined
              }
              actions={[]}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
